import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Plus, Upload, Loader2, ImageIcon } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/banners")({
  component: BannersAdmin,
});

const BUCKET = "app-assets";
const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_BYTES = 5 * 1024 * 1024; // 5MB

function BannersAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin_banners"],
    queryFn: async () => (await supabase.from("banners").select("*").order("position")).data ?? [],
  });

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [position, setPosition] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function resetForm() {
    setTitle("");
    setPosition(0);
    setFile(null);
    setPreviewUrl(null);
    setProgress(0);
    setUploading(false);
    setSaving(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  function onPick(f: File | null) {
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPG, PNG, or WEBP allowed");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image must be under 5MB");
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  }

  async function uploadImage(f: File): Promise<string | null> {
    setUploading(true);
    setProgress(10);
    const ext = (f.name.split(".").pop() || "png").toLowerCase();
    const path = `banners/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    // Simulated progress since supabase-js v2 doesn't expose upload progress
    const tick = setInterval(() => setProgress((p) => Math.min(p + 12, 85)), 180);
    const { error } = await supabase.storage.from(BUCKET).upload(path, f, { upsert: false, cacheControl: "3600", contentType: f.type });
    clearInterval(tick);
    if (error) {
      setUploading(false);
      setProgress(0);
      toast.error(error.message);
      return null;
    }
    setProgress(92);
    const { data: signed, error: sErr } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (sErr || !signed) {
      setUploading(false);
      setProgress(0);
      toast.error(sErr?.message || "Failed to sign URL");
      return null;
    }
    setProgress(100);
    setUploading(false);
    return signed.signedUrl;
  }

  async function save() {
    if (!file) return toast.error("Please choose an image");
    setSaving(true);
    const url = await uploadImage(file);
    if (!url) { setSaving(false); return; }
    const { error } = await supabase.from("banners").insert({
      title: title.trim() || null,
      image_url: url,
      link_url: null,
      position: Number(position) || 0,
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Banner added");
    resetForm();
    setOpen(false);
    qc.invalidateQueries();
  }

  async function toggle(id: string, active: boolean) {
    await supabase.from("banners").update({ active }).eq("id", id);
    qc.invalidateQueries();
  }
  async function del(id: string) {
    if (!confirm("Delete banner?")) return;
    await supabase.from("banners").delete().eq("id", id);
    qc.invalidateQueries();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Banners</h1>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gradient-primary shadow-purple"><Plus className="mr-2 h-4 w-4" /> Add Banner</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full">
            <DialogHeader><DialogTitle>New Banner</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional banner title" />
              </div>

              <div>
                <Label>Upload Image</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => onPick(e.target.files?.[0] ?? null)}
                />
                {previewUrl ? (
                  <div className="mt-2 overflow-hidden rounded-xl border-2 border-primary/30">
                    <img src={previewUrl} alt="preview" className="aspect-[16/8] w-full object-cover" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="mt-2 flex aspect-[16/8] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 text-primary transition hover:bg-primary/10"
                  >
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-sm font-semibold">Tap to choose image</span>
                    <span className="text-[11px] text-muted-foreground">JPG, PNG, WEBP · up to 5MB</span>
                  </button>
                )}
                <div className="mt-2 flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" /> {file ? "Change image" : "Choose image"}
                  </Button>
                  {file && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setFile(null); setPreviewUrl(null); if (fileRef.current) fileRef.current.value = ""; }}>
                      Remove
                    </Button>
                  )}
                </div>
                {uploading && (
                  <div className="mt-2">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full gradient-primary transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">Uploading… {progress}%</p>
                  </div>
                )}
              </div>

              <div>
                <Label>Position</Label>
                <Input type="number" inputMode="numeric" value={position} onChange={(e) => setPosition(Number(e.target.value))} />
              </div>

              <Button onClick={save} disabled={saving || uploading || !file} className="w-full gradient-primary shadow-purple">
                {saving || uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save Banner
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(data ?? []).map((b: any) => (
          <div key={b.id} className="overflow-hidden rounded-2xl bg-card shadow-card">
            <img src={b.image_url} alt="" className="aspect-[16/8] w-full object-cover" />
            <div className="flex items-center justify-between p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{b.title || "Untitled"}</p>
                <p className="text-[11px] text-muted-foreground">Position {b.position}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={b.active} onCheckedChange={(v) => toggle(b.id, v)} />
                <button onClick={() => del(b.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
